import { useCallback, useEffect, useState } from "react";
import { FileSpreadsheet, Loader2, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApi, rupiah, STATUS_META } from "@/lib/api";
import OrderDetailDialog from "@/components/admin/OrderDetailDialog";

const FILTERS = [["", "Semua"], ["menunggu", "Menunggu"], ["terverifikasi", "Terverifikasi"], ["terkirim", "Terkirim"], ["ditolak", "Ditolak"]];

export const StatusBadge = ({ status }) => (
  <span className={`border px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${STATUS_META[status]?.cls}`}>{STATUS_META[status]?.label}</span>
);

function Stats({ stats }) {
  const items = [["Total Pesanan", stats.total], ["Menunggu", stats.menunggu], ["Terverifikasi", stats.terverifikasi], ["Terkirim", stats.terkirim], ["Pendapatan", rupiah(stats.revenue)]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10 mt-4">
      {items.map(([l, v]) => (
        <div key={l} className="bg-[#0d0d0d] p-4" data-testid={`stat-${l.toLowerCase().replace(/ /g, "-")}`}>
          <p className="text-xs uppercase text-neutral-400">{l}</p>
          <p className="font-display font-black text-3xl mt-1">{v ?? 0}</p>
        </div>
      ))}
    </div>
  );
}

export default function OrdersTab({ canVerify, isAdmin, refreshKey }) {
  const api = useApi();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const [o, s] = await Promise.all([api("get", "/admin/orders", null, { params: { status: status || undefined, q: q || undefined } }), api("get", "/admin/stats")]);
    setOrders(o.data); setStats(s.data);
  }, [api, status, q]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api("get", "/admin/orders/export", null, { params: { status: status || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Pesanan-LensaJuara-${new Date().toISOString().slice(0, 10)}${status ? `-${status}` : ""}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("File Excel diunduh");
    } catch { toast.error("Gagal mengekspor"); } finally { setExporting(false); }
  };

  return (
    <div>
      <Stats stats={stats} />
      <div className="flex flex-wrap items-center gap-2 mt-6">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} data-testid={`filter-${v || "all"}`}
            className={`px-3 py-1.5 text-sm border transition-colors ${status === v ? "bg-[#FF5A00] border-[#FF5A00]" : "border-white/15 hover:border-white/40"}`}>{l}</button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kode, BIB, nama, klub..." data-testid="orders-search-input" className="pl-9 h-9 w-64 bg-[#121212]" />
        </div>
        <button onClick={load} data-testid="orders-refresh-btn" className="p-2 border border-white/15 hover:border-white/40"><RefreshCw className="w-4 h-4" /></button>
        <button onClick={exportXlsx} disabled={exporting} data-testid="export-excel-btn" className="h-9 px-3 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-60 flex items-center gap-2 text-sm font-semibold transition-colors">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Ekspor Excel
        </button>
      </div>
      <div className="border border-white/10 mt-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Kode</TableHead><TableHead>Tanggal</TableHead><TableHead>Atlet</TableHead><TableHead>Pemesan</TableHead>
              <TableHead>Lomba</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-10" data-testid="orders-empty">Belum ada pesanan</TableCell></TableRow>}
            {orders.map((o) => (
              <TableRow key={o.id} onClick={() => setSelected(o)} data-testid={`order-row-${o.order_code}`} className="cursor-pointer border-white/10 hover:bg-[#FF5A00]/5">
                <TableCell className="font-display font-bold text-base">{o.order_code}</TableCell>
                <TableCell className="text-xs text-neutral-400 whitespace-nowrap">{new Date(o.created_at).toLocaleString("id-ID")}</TableCell>
                <TableCell>{o.athlete_name}<div className="text-xs text-neutral-500">BIB {o.bib_number || "-"} · {o.club}</div></TableCell>
                <TableCell>{o.parent_name}<div className="text-xs text-neutral-500">{o.whatsapp}</div></TableCell>
                <TableCell>{o.event_count}</TableCell>
                <TableCell className="whitespace-nowrap">{rupiah(o.total)}</TableCell>
                <TableCell><StatusBadge status={o.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <OrderDetailDialog order={selected} onClose={() => setSelected(null)} canVerify={canVerify} isAdmin={isAdmin} onDeleted={() => { setSelected(null); load(); }} onUpdated={(o) => { setSelected(o); load(); }} />
    </div>
  );
}
